/** @file Sets or clears the default crossfade (seconds) from one animation or @DIR base to another. */
import { z } from 'zod';
import type { Draft } from 'immer';
import type { Doc } from '@pose2d/format/types';
import { ids } from '@pose2d/format/ids';
import { mixNames } from '@pose2d/format/doc/mixNames';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';

/** The stored side for `ref`: an animation id becomes its name; a name must be an animation or a @DIR base. */
function mixSide(draft: Draft<Doc>, ref: string, path: 'from' | 'to', ctx: CommandContext): string {
  if (ids.isId(ref)) {
    const name = ctx.index.kindOf.get(ref) === 'animation' ? draft.animations[ref]?.name : undefined;
    if (name !== undefined) return name;
  } else if (mixNames(draft.animations).has(ref)) {
    return ref;
  }
  return throwError('NOT_FOUND', { kind: 'animation', ref }, { path, hint: 'name an animation (or its id) or the base of a <base>@<DIR> animation' });
}

/** animation.setMixDefault: `mixDefaults["from>to"] = seconds`; null removes the pair; the same seconds is a no-op. */
export const command = defineCommand({
  name: 'animation.setMixDefault',
  description: 'Set the default crossfade in seconds used when a track switches from `from` to `to` (animation names, @DIR bases or ids); seconds null clears the pair.',
  input: z.object({ from: z.string().min(1), to: z.string().min(1), seconds: prim.finite.nonnegative().nullable() }).strict(),
  apply(draft, input, ctx) {
    const pair = `${mixSide(draft, input.from, 'from', ctx)}>${mixSide(draft, input.to, 'to', ctx)}`;
    if (draft.mixDefaults?.[pair] === input.seconds) {
      ctx.warnings.push({ code: 'INFO', message: `${pair} already mixes over ${input.seconds} s; nothing changed`, params: { pair }, path: 'seconds' });
      return;
    }
    if (input.seconds !== null) {
      (draft.mixDefaults ??= {})[pair] = input.seconds;
    } else if (draft.mixDefaults && Object.hasOwn(draft.mixDefaults, pair)) {
      Reflect.deleteProperty(draft.mixDefaults, pair);
    } else {
      ctx.warnings.push({ code: 'INFO', message: `no mix default is set for ${pair}`, params: { pair }, path: 'seconds' });
      return;
    }
    draft.meta.updatedAt = ctx.now;
  },
});
